import { getFormProps, useForm } from '@conform-to/react'
import { Popover, Transition } from '@headlessui/react'
import {
	Form,
	Link,
	type MetaFunction,
	NavLink,
	json,
	useSubmit,
	Outlet,
	useLoaderData,
	useMatches,
	useFetcher,
} from '@remix-run/react'
import {
	ActionFunctionArgs,
	type LoaderFunctionArgs,
} from '@remix-run/server-runtime'
import clsx from 'clsx'
import { Fragment, useRef } from 'react'
import { z } from 'zod'
import { Button } from '#app/components/ui/button.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu.tsx'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '#app/components/ui/tooltip.tsx'

import { Icon } from '#app/components/ui/icon'
import { type action, useOptimisticThemeMode } from '#app/root'
import { getUserId, logout } from '#app/utils/auth.server'
import { getHints } from '#app/utils/client-hints'
import { prisma } from '#app/utils/db.server.ts'
import { getEnv } from '#app/utils/env.server'
import { honeypot } from '#app/utils/honeypot.server'
import { combineHeaders, getDomainUrl, getUserImgSrc } from '#app/utils/misc'
import { Theme, getTheme, setTheme } from '#app/utils/theme.server'
import { makeTimings, time } from '#app/utils/timing.server.ts'
import { getToast } from '#app/utils/toast.server'
import { useOptionalUser, useUser } from '#app/utils/user'

import { brands, colours } from './images/brands/brands.ts'
import coloursLogo from './images/brands/colours.svg'

import { parseWithZod } from '@conform-to/zod'
import { invariantResponse } from '@epic-web/invariant'
import coloursStyleSheetUrl from '#app/styles/colours.css?url'
import { LinksFunction } from '@remix-run/node'

export const meta: MetaFunction = () => [{ title: 'Colours Exposion 2024' }]

export const links: LinksFunction = () => {
	return [
		{ rel: 'stylesheet', href: coloursStyleSheetUrl },
		{ rel: 'stylesheet', href: 'https://rsms.me/inter/inter.css' },
	].filter(Boolean)
}

export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUniqueOrThrow({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { id: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/colours' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = honeypot.getInputProps()

	return json(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, {
		schema: ThemeFormSchema,
	})

	invariantResponse(submission.status === 'success', 'Invalid theme received')

	const { theme } = submission.value

	const responseInit = {
		headers: { 'set-cookie': setTheme(theme) },
	}
	return json({ result: submission.reply() }, responseInit)
}

export default function Layout() {
	return (
		<>
			<div className="bg-slate-900 text-slate-300">
				<Header />
				<Outlet />
				<Brands />
				<Footer />
			</div>
		</>
	)
}

function Header() {
	return (
		<header className="pt-3">
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<nav className="relative z-50 flex justify-between">
					<div className="flex items-center md:gap-x-12">
						<Link to="#" aria-label="Home">
							<HeaderLogo />
						</Link>
						<div className="hidden md:flex md:gap-x-6">
							<NavLink
								className="text-xl font-semibold text-rose-500"
								to="/colours#whatis"
							>
								What's Colours?
							</NavLink>
							<NavLink
								className="text-xl font-semibold text-fuchsia-500"
								to="/colours#whatson"
							>
								What's On?
							</NavLink>
							<NavLink
								className="text-xl font-semibold text-blue-500"
								to="/colours#features"
							>
								The Experience of Colours!
							</NavLink>
						</div>
					</div>
					<div className="flex items-center gap-x-5 md:gap-x-8">
						<div className="hidden md:block">
							<div className="flex flex-col justify-between">
								<header className="container">
									<nav className="flex flex-wrap items-center justify-between gap-4 sm:flex-nowrap md:gap-8">
									</nav>
								</header>
							</div>
						</div>
						<Link to="#">
							<Button color="blue">
								<span>
									Reserve tickets{' '}
									<span className="hidden lg:inline">soon!</span>
								</span>
							</Button>
						</Link>

						<div className="-mr-1 md:hidden">
							<MobileNavigation />
						</div>
					</div>
				</nav>
			</div>
		</header>
	)
}

function HeaderLogo() {
	return (
		<Link to="/colours" className="pt-2">
<img className="w-44" src={coloursLogo} alt="" />
		</Link>
	)
}

function MobileNavLink({
	href,
	children,
}: {
	href: string
	children: React.ReactNode
}) {
	return (
		<Popover.Button as={Link} to={href} className="block w-full p-2">
			{children}
		</Popover.Button>
	)
}

function MobileNavIcon({ open }: { open: boolean }) {
	return (
		<svg
			aria-hidden="true"
			className="h-3.5 w-3.5 overflow-visible stroke-slate-700"
			fill="none"
			strokeWidth={2}
			strokeLinecap="round"
		>
			<path
				d="M0 1H14M0 7H14M0 13H14"
				className={clsx(
					'origin-center transition',
					open && 'scale-90 opacity-0',
				)}
			/>
			<path
				d="M2 2L12 12M12 2L2 12"
				className={clsx(
					'origin-center transition',
					!open && 'scale-90 opacity-0',
				)}
			/>
		</svg>
	)
}

function MobileNavigation() {
  return (
    <Popover>
      <Popover.Button
        className="relative z-10 flex h-8 w-8 items-center justify-center ui-not-focus-visible:outline-none"
        aria-label="Toggle Navigation"
      >
        {({ open }) => <MobileNavIcon open={open} />}
      </Popover.Button>
      <Transition.Root>
        <Transition.Child
          as={Fragment}
          enter="duration-150 ease-out"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="duration-150 ease-in"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Popover.Overlay className="fixed inset-0 bg-slate-600/50" />
        </Transition.Child>
        <Transition.Child
          as={Fragment}
          enter="duration-150 ease-out"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="duration-100 ease-in"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
					<Popover.Panel
						as="div"
						className="border border-white bg-slate-900 absolute inset-x-0 top-full mt-4 flex origin-top flex-col rounded-2xl p-4 text-lg tracking-tight shadow-xl ring-1 ring-slate-900/5"
					>
						<MobileNavLink href="/colours#whatis">
							What's Colours?
						</MobileNavLink>
						<MobileNavLink href="/colours#whatson">What's On</MobileNavLink>
						<MobileNavLink href="/colours#features">
							The Experience of Colours
						</MobileNavLink>

					</Popover.Panel>
        </Transition.Child>
      </Transition.Root>
    </Popover>
  )
}

function Brands() {
	return (
		<div className="p-6">
			<div>
				<h2 className="font-display md:text-center animate-gradient bg-[linear-gradient(to_right,theme(colors.indigo.400),theme(colors.indigo.100),theme(colors.sky.400),theme(colors.fuchsia.400),theme(colors.sky.400),theme(colors.indigo.100),theme(colors.indigo.400))]  bg-[length:200%_auto] bg-clip-text text-3xl font-bold tracking-tight text-transparent sm:text-4xl">
				With Thanks To{' '}
					</h2>
			</div>
			<div className="mx-auto max-w-6xl px-6 lg:px-8">
				<ul className="-mx-6 grid grid-cols-4 place-items-center gap-0.5 overflow-hidden sm:mx-0 sm:rounded-2xl">
					<TooltipProvider>
						{brands.map((brand, i) => (
							<li
								key={brand.href}
								className="animate-roll-reveal [animation-fill-mode:backwards]"
								style={{ animationDelay: `${i * 0.07}s` }}
							>
								<Tooltip>
									<TooltipTrigger asChild>
										<a
											href={brand.href}
											className="grid size-20 h-36 place-items-center rounded-2xl object-cover transition hover:-rotate-6  sm:size-24"
										>
											<img className="" src={brand.src} alt="" />
										</a>
									</TooltipTrigger>
									<TooltipContent>{brand.alt}</TooltipContent>
								</Tooltip>
							</li>
						))}
					</TooltipProvider>
				</ul>
			</div>
		</div>
	)
}

function Footer() {
	return (
		<footer className="">
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<div className="lg:py-16">
					<div className="text-center">
						<div className="text-2xl">
							<FooterLogo />
						</div>

						<p className="p-2">
							Colours Explosion is where feters comes together to show Grenada
							how to party for Spicemas 24!
						</p>
					</div>
					<nav className="mt-2 text-sm" aria-label="quick links">
						<div className="flex items-center justify-center gap-x-6 py-6">
							<a
								href="https://www.instagram.com/feelfreepromo/"
								target="_blank"
								className="group"
								aria-label="Colours Explosion on Instagram"
								rel="noreferrer"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									className="h-12 w-12 fill-white"
									viewBox="0 0 50 50"
								>
									<path d="M16 3C8.832 3 3 8.832 3 16v18c0 7.168 5.832 13 13 13h18c7.168 0 13-5.832 13-13V16c0-7.168-5.832-13-13-13H16zm0 2h18c6.086 0 11 4.914 11 11v18c0 6.086-4.914 11-11 11H16C9.914 45 5 40.086 5 34V16C5 9.914 9.914 5 16 5zm21 6a2 2 0 0 0-2 2 2 2 0 0 0 2 2 2 2 0 0 0 2-2 2 2 0 0 0-2-2zm-12 3c-6.063 0-11 4.937-11 11s4.937 11 11 11 11-4.937 11-11-4.937-11-11-11zm0 2c4.982 0 9 4.018 9 9s-4.018 9-9 9-9-4.018-9-9 4.018-9 9-9z" />
								</svg>
							</a>
							<a
								href="https://www.facebook.com/feelfreepromo/"
								target="_blank"
								className="group"
								aria-label="Colours Explosion on Facebook"
								rel="noreferrer"
							>
								{' '}
								<svg
									xmlns="http://www.w3.org/2000/svg"
									className="h-12 w-12 fill-white"
									viewBox="0 0 50 50"
								>
									<path d="M25 3C12.862 3 3 12.862 3 25c0 11.02 8.128 20.138 18.713 21.729l1.148.173V29.566h-5.197v-3.52h5.197v-4.673c0-2.878.691-4.774 1.834-5.963 1.144-1.19 2.833-1.789 5.184-1.789 1.88 0 2.611.114 3.307.2v2.88h-2.448a3.59 3.59 0 0 0-3.119 1.807c-.591 1.032-.775 2.264-.775 3.52v4.017h6.123l-.545 3.52h-5.578V46.93l1.135-.155C38.714 45.32 47 36.127 47 25c0-12.138-9.862-22-22-22zm0 2c11.058 0 20 8.942 20 20 0 9.73-6.964 17.732-16.156 19.533V31.564h5.293l1.162-7.52h-6.455v-2.017c0-1.037.19-1.967.51-2.525.32-.558.628-.8 1.384-.8h4.448V12.01l-.868-.117c-.6-.082-1.969-.272-4.44-.272-2.702 0-5.022.736-6.624 2.402-1.602 1.666-2.393 4.147-2.393 7.35v2.674h-5.197v7.52h5.197V44.47C11.817 42.555 5 34.624 5 25 5 13.942 13.942 5 25 5z" />
								</svg>
							</a>
						</div>

						<div className="flex justify-center gap-x-6 pb-2">
							<NavLink to="/colours#whatis" className="">What Is Colours?</NavLink>
							<NavLink to="/colours#whatson" className="">What's On?</NavLink>
							<NavLink to="/colours#features" className="">The Experience of Colours</NavLink>
							<NavLink to="/colours/tickets" className="">Tickets</NavLink>
						</div>
					</nav>
				</div>
				<div className="flex border-t border-slate-600 p-4 mt-2 sm:justify-between">
					<p className="text-sm sm:text-base">
						Copyright &copy; {new Date().getFullYear()} Barrels Grenada.
					</p>
				</div>
			</div>
		</footer>
	)
}

function FooterLogo() {
	return (
		<div className="flex items-center justify-center">
		<Link to="/colours" className="pt-2">
<img className="w-72" src={coloursLogo} alt="" />
		</Link>
		</div>

	)
}
